import { watch } from 'vue';
import { useMounted, useScroll } from '@vueuse/core';
import { unitUtils } from '@/utils';
import { useStore } from 'vuex';

export default function useScrollToItem(
  containerReference,
  itemId,
  options = {},
  onBeforeScroll = null,
  onAfterScroll = null
) {
  const { scrollToBottom = false, ...useScrollOptions } = options;
  const { y } = useScroll(containerReference, {
    behavior: 'smooth',
    ...useScrollOptions,
  });
  const isMounted = useMounted();
  const store = useStore();

  const safeSetScrollTop = (position = 0) => {
    const element = containerReference.value;
    if (!element) return;
    // Prefer native scrollTo if available; otherwise, fall back to setting scrollTop directly.
    if (typeof element.scrollTo === 'function') {
      try {
        element.scrollTo({ top: position, behavior: 'auto' });
      } catch (error) {
        // Some environments (e.g., tests) may not fully support scroll options
        try {
          element.scrollTo(0, position);
        } catch (_) {
          element.scrollTop = position;
        }
      }
    } else {
      element.scrollTop = position;
    }
  };

  const scrollToItem = () => {
    const element = document.getElementById(itemId.value);
    if (typeof onBeforeScroll === 'function') {
      onBeforeScroll?.(itemId.value);
    }
    if (element && containerReference.value) {
      const position =
        element.offsetTop -
        containerReference.value.offsetTop -
        unitUtils.convertRemToPixels(1);
      // If VueUse useScroll target supports scrollTo, update y; else set scrollTop directly
      if (typeof containerReference.value.scrollTo === 'function') {
        y.value = position;
      } else {
        safeSetScrollTop(position);
      }
    }
    if (typeof onAfterScroll === 'function') {
      setTimeout(onAfterScroll, 6000);
    }
  };

  const scrollToContainerBottom = () => {
    const height = containerReference.value?.scrollHeight ?? 0;
    if (typeof containerReference.value?.scrollTo === 'function') {
      y.value = height;
    } else {
      safeSetScrollTop(height);
    }
  };

  watch(
    () => [
      itemId.value,
      isMounted.value,
      store.getters['appTraffic/isLoading'],
    ],
    async ([newItemId, newIsMounted]) => {
      if (!newIsMounted) return;
      if (newItemId) {
        await scrollToItem();
      } else if (!newItemId && scrollToBottom) {
        scrollToContainerBottom();
      }
    },
    {
      immediate: true,
      deep: true,
    }
  );

  return { scrollToItem, scrollToBottom: scrollToContainerBottom };
}
